import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  GoogleMap,
  Marker,
  TrafficLayer,
  useJsApiLoader,
  OverlayView,
  InfoWindow,
} from "@react-google-maps/api";
import ReactMapGL, { Marker as MapboxMarker, Layer, Source } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { db } from "../../firebase";
import { auth } from "../../firebase";
import { loadHydrantsForArea } from "../../utils/hydrantPoiLoader";
import { deriveHydrantDocKey } from "../../utils/globalPoiUtils";
import { hydrantIconSvg, hydrantIconDataUri, HYDRANT_ICON_W, HYDRANT_ICON_H } from "../../utils/hydrantIcon";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  doc,
  onSnapshot,
  getDoc,
} from "firebase/firestore";
import { getDatabase, ref, onValue } from "firebase/database";
import FloatingMapCard from "../FloatingMapCard/floatingMapCard";
import alarmSound from "./fire_emergency.mp3";
import { playAlarmTone } from "../../utils/alarmTone";
import callmarker from "./call.png";
import callmarkerfire from "./firepin.png";
import callmarkerems from "./emspin.png";
import ScrollingText from "../ScrollingText/ScrollingText";
const showStyle = {
  display: "block",
};
const hideStyle = {
  display: "none",
};

const FullScreenMap = ({ alarms, setAlarms, selectedTab, setSelectedTab }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [agency, setAgency] = useState(null);
  const [agencyLocation, setAgencyLocation] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [floatingMapCardText, setFloatingMapCardText] = useState(null);
  const [posts, setPosts] = useState([]);
  const [activePostIndex, setActivePostIndex] = useState(0);
  const [postText, setPostText] = useState("");
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0);
  const [delayedAudio, setDelayedAudio] = useState(false);
  const [hasCallins, setHasCallins] = useState(false);
  const [agencyLogoUrl, setAgencyLogoUrl] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [hydrants, setHydrants] = useState([]);
  const [secondaryAgencies, setSecondaryAgencies] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [callerLocation, setCallerLocation] = useState(null);
  const [useGoogleMaps, setUseGoogleMaps] = useState(false); // Default to Mapbox
  const [mapboxError, setMapboxError] = useState(false); // Track Mapbox errors for failover
  const mapRef = useRef(null);
  const currentIndexRef = useRef(-1); // Use ref to persist index across re-renders

  const hasInvalidAddress = alarms.length > 0 && (!alarms[currentMarkerIndex]?.Latitude || alarms[currentMarkerIndex]?.Latitude === "" || !alarms[currentMarkerIndex]?.Longitude || alarms[currentMarkerIndex]?.Longitude === "");

  // Always load Google Maps API (only actually loaded if useGoogleMaps becomes true)
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, 
    libraries: [],
    id: 'google-map-script',
  });
  
  // Log when Google Maps API is loaded
  useEffect(() => {
    if (useGoogleMaps && isLoaded) {
      console.log('🗺️ Google Maps API LOADED');
    }
    if (useGoogleMaps && loadError) {
      console.error('❌ Google Maps API LOAD ERROR:', loadError);
    }
  }, [isLoaded, loadError, useGoogleMaps]);
  
  useEffect(() => {
    setDelayedAudio(true);

    const refreshInterval = setInterval(() => {
      setTimeout(() => {
        setDelayedAudio(false);
      }, 15000);

      window.location.reload();
    }, 60 * 60 * 1000); 

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    if (agency) {
      const fetchAgencySettings = async () => {
        const settingsCollectionRef = collection(db, agency + " Settings");
  
        onSnapshot(settingsCollectionRef, (querySnapshot) => {
          const data = querySnapshot.docs.map((doc) => doc.data());
          if (data && data.length > 0) {
            setAgencyLogoUrl(data[0].logo);
          } else {
            console.error("No agency settings found");
          }
        });
      };
  
      fetchAgencySettings();
    }
  }, [agency]);

  useEffect(() => {
    if (agency) {
      const infoDocRef = doc(db, "Agencies", agency, "Settings", "Info");

getDoc(infoDocRef)
  .then((docSnapshot) => {
    if (docSnapshot.exists()) {
      const infoData = docSnapshot.data();
      setAgencyLocation({
        lat: parseFloat(infoData.lat),
        lng: parseFloat(infoData.long),
      });
      console.log('Fetched agencyLocation from database:', {
        lat: parseFloat(infoData.lat),
        lng: parseFloat(infoData.long),
      });
      console.log("Agency location fetched:", {
        lat: parseFloat(infoData.lat),
        lng: parseFloat(infoData.long),
      });
    } else {
      console.error("Info document does not exist");
    }
  })
  .catch((error) => {
    console.error("Error fetching agency info:", error);
  });

     
    }
  }, [agency]);


  
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const unsubscribe = onSnapshot(userDocRef, (doc) => {
          if (!doc.exists()) {
          } else {
            const dataObj = doc.data();

            setAgency(dataObj.agency);
            setSecondaryAgencies(Array.isArray(dataObj.secondaryagencys) ? dataObj.secondaryagencys : []);

            if (dataObj.disabled) {
              // Replace this with your navigation logic
            }
          }
        });

        return () => {
          unsubscribe();
        };
      }
    });
    return () => {
      unsubscribeAuth();
    };
  }, []);

  useEffect(() => {
    if (agency) {
      const currentTime = new Date();
      const threeHoursAgo = new Date(currentTime.getTime() - 3 * 60 * 60 * 1000);
      const oneHourAgo = new Date(currentTime.getTime() - 60 * 60 * 1000);
      
      const threeHoursAgoTimestamp = Timestamp.fromDate(threeHoursAgo);
      const oneHourAgoTimestamp = Timestamp.fromDate(oneHourAgo);

      // Create a Firestore query for alarms
      const alarmsCollection = collection(db, agency + " Alarms");
      const alarmsQuery = query(
        alarmsCollection,
        where("TimeStamp", ">=", threeHoursAgoTimestamp),
      );

      const unsubscribeAlarms = onSnapshot(alarmsQuery, (querySnapshot) => {
        querySnapshot.docChanges().forEach((change) => {
          const data = change.doc.data();
          if (change.type === "added" && !delayedAudio) {
            playAlarmTone(alarmSound);
          }
        });

        const filteredData = querySnapshot.docs
          .map(doc => {
            const data = doc.data();
            const docTimeStamp = data.TimeStamp.toDate();
            
            // Apply the same filtering logic as provided
            if (
              (data.hasOwnProperty('AlarmStatus') && data.AlarmStatus === 'true') || 
              (!data.hasOwnProperty('AlarmStatus') && docTimeStamp >= oneHourAgo) || 
              (data.callcard === '*DISPATCH*' && docTimeStamp >= oneHourAgo)
            ) {
              return { ...data, id: doc.id };
            }
            return null;
          })
          .filter(Boolean); // Remove null entries

        getMarkers(filteredData);
      });

      // Try nested structure first: Agencies/{agency}/Station Board
      const nestedDeptCollection = collection(db, "Agencies", agency, "Station Board");
      const nestedDeptQuery = query(nestedDeptCollection);
      const unsubscribeNestedDept = onSnapshot(nestedDeptQuery, (querySnapshot) => {
        const data = querySnapshot.docs.map((doc) => doc.data());
        console.log('📄 Firestore nested Station Board query returned:', data.length, 'documents');
        if (data && data.length > 0) {
          console.log('📄 Firestore Station Board document data:', data[0]);
          if (data[0].stationLat && data[0].stationLng) {
            setCurrentLocation({ lat: parseFloat(data[0].stationLat), lng: parseFloat(data[0].stationLng) });
          }
          // Check if googleMaps boolean exists, default to false (use Mapbox)
          if (data[0].hasOwnProperty('googleMaps')) {
            const shouldUseGoogle = data[0].googleMaps === true;
            // Only use Firestore setting if Mapbox hasn't errored
            if (!mapboxError) {
              setUseGoogleMaps(shouldUseGoogle);
            }
            console.log('🗺️ Map provider setting from Firestore - googleMaps field:', data[0].googleMaps, 'Type:', typeof data[0].googleMaps);
            console.log('🗺️ Setting useGoogleMaps to:', mapboxError ? true : shouldUseGoogle);
          } else {
            // If Mapbox has errored, use Google Maps regardless
            setUseGoogleMaps(mapboxError ? true : false);
            console.log('🗺️ No googleMaps setting found, defaulting to', mapboxError ? 'Google Maps (Mapbox failover)' : 'Mapbox');
          }
        }
      });
      
      // Fallback: Try flat structure: "{agency} Station Board"
      const flatDeptCollection = collection(db, agency + " Station Board");
      const flatDeptQuery = query(flatDeptCollection);
      const unsubscribeFlatDept = onSnapshot(flatDeptQuery, (querySnapshot) => {
        const data = querySnapshot.docs.map((doc) => doc.data());
        console.log('📄 Firestore flat Station Board query returned:', data.length, 'documents');
        // Only use flat data if nested didn't return anything
        if (data && data.length > 0) {
          if (data[0].stationLat && data[0].stationLng) {
            setCurrentLocation({ lat: parseFloat(data[0].stationLat), lng: parseFloat(data[0].stationLng) });
          }
        }
      });

      // Create a Firestore query for the last 24 hours

      const postsCollection = collection(db, agency);
      const postsQuery = query(
        postsCollection,
        where("privacy", "in", ["All", "Stationboard"])
    );
      const unsubscribePosts = onSnapshot(postsQuery, (querySnapshot) => {
        const data = querySnapshot.docs.map((doc) => doc.data());
        const postsWithSeconds = data.filter(
          (post) => post.postTime && post.postTime.seconds
        );

        postsWithSeconds.sort((a, b) => {
          return b.postTime.seconds - a.postTime.seconds;
        });
        let newPosts = postsWithSeconds.slice(0, 5);

        setPosts(newPosts);
      });

      return () => {
        unsubscribeAlarms();
        unsubscribeNestedDept();
        unsubscribeFlatDept();
        unsubscribePosts();
      };
    }
  }, [agency]);

  // Load units from agencies
  useEffect(() => {
    // Function to load units from a single agency
    const loadUnitsFromAgency = (agencyId) => {
      const unitsRef = ref(getDatabase(), `/UnitLocation/${agencyId}`);
      
      return onValue(unitsRef, (snapshot) => {
        const unitsData = snapshot.val();
        
        if (unitsData) {
          // Calculate the timestamp for 24 hours ago
          const oneDayAgo = new Date();
          oneDayAgo.setHours(oneDayAgo.getHours() - 24);
          const oneDayAgoTimestamp = oneDayAgo.getTime();
          
          // Create unit array and filter by timestamp
          const unitArray = Object.keys(unitsData)
            .map(key => ({
              responderName: key,
              agency: agencyId, // Add agency ID to track which agency each unit belongs to
              ...unitsData[key],
            }))
            .filter(unit => {
              // Check if the unit has a valid timestamp
              const unitTimestamp = unit.timestamp || 0;
              
              // Keep only units updated within the last 24 hours
              const isRecent = unitTimestamp >= oneDayAgoTimestamp;
              
              return isRecent;
            });
          
          // Merge with existing units
          setVehicles(prevVehicles => {
            // Filter out any previous units from this agency to avoid duplicates
            const filteredPrevVehicles = prevVehicles.filter(v => v.agency !== agencyId);
            return [...filteredPrevVehicles, ...unitArray];
          });
        }
      });
    };
    
    // Track all database listeners to unsubscribe later
    const listeners = [];
    
    // Load units from the agency (simplified version for FullScreenMap)
    if (agency) {
      listeners.push(loadUnitsFromAgency(agency));
    }
    
    // Return cleanup function to unsubscribe from all listeners
    return () => {
      listeners.forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [agency]);

  const extractCallerCoordinates = useCallback((eventComments) => {
    console.log('Extracting caller coordinates from Event Comments:', eventComments);
    
    // Define a regular expression pattern to match coordinates
    const regex = /-?\d+\.\d+\s+-?\d+\.\d+/;
    
    if (!eventComments) {
      console.log('No Event Comments available.');
      return;
    }

    // Find matches in the EventComments text
    const matches = eventComments.match(regex);
    console.log('Regex matches found:', matches);
    
    if (matches && matches.length > 0) {
      // The first match contains the coordinates
      const coordinates = matches[0];
      console.log('Extracted coordinates string:', coordinates);
    
      // Parse the coordinates
      const [longitudeStr, latitudeStr] = coordinates.split(' ');
      const latitude = parseFloat(latitudeStr);
      const longitude = parseFloat(longitudeStr);
      
      console.log('Parsed latitude:', latitude);
      console.log('Parsed longitude:', longitude);
    
      if (!isNaN(latitude) && !isNaN(longitude)) {
        // Save the coordinates in state
        console.log('Setting callerLocation state:', { latitude, longitude });
        setCallerLocation({ latitude, longitude });
      } else {
        console.log('Invalid coordinates extracted.');
      }
    } else {
      console.log('No coordinates found in Event Comments.');
    }
  }, []);

  // Debug: Monitor callerLocation state changes
  useEffect(() => {
    console.log('CallerLocation state updated:', callerLocation);
  }, [callerLocation]);

  async function getMarkers(data) {
    const updatedData = await Promise.all(
      data.map(async (alarm) => {
        // Parse latitude and longitude from Firestore data
        let latitude = parseFloat(alarm.Latitude);
        let longitude = parseFloat(alarm.Longitude);
  
        if (isNaN(latitude) || isNaN(longitude)) {
          // Coordinates are missing or invalid - serverside geocoding should have handled this
          console.log(`Missing coordinates for alarm ID ${alarm.id}, address: ${alarm.Address}`);
        } else {
          // Coordinates are valid, use them
          console.log(`Using coordinates from Firestore for alarm ID ${alarm.id}: ${latitude}, ${longitude}`);
        }

      // Update the alarm object with the latitude and longitude
      alarm.lat = latitude;
      alarm.lng = longitude;

        const callinsRef = collection(db, `${agency} Alarms/${alarm.id}/Callins`);
        const callinsSnapshot = await getDocs(callinsRef);
        alarm.Callins = callinsSnapshot.docs.map((callinDoc) => {
          const callinData = callinDoc.data();
          return {
            ...callinData,
            qualifications: callinData.qualifications || "",
          };
        });

        onSnapshot(callinsRef, (snapshot) => {
          const updatedCallins = snapshot.docs.map((callinDoc) => {
            const callinData = callinDoc.data();
            return {
              ...callinData,
              qualifications: callinData.qualifications || "",
            };
          });

          alarm.Callins = updatedCallins;
          setAlarms((prevAlarms) => {
            const updatedAlarms = prevAlarms.map((prevAlarm) =>
              prevAlarm.id === alarm.id ? { ...prevAlarm, Callins: updatedCallins } : prevAlarm
            );
            return updatedAlarms;
          });
        });

        return alarm;
      })
    );
    setAlarms(updatedData);
  }
  useEffect(() => {
    const timer = setInterval(async () => {
      if (alarms.length > 0) {
        await checkAlarms();
  
        // Calculate the index of the next active marker
        currentIndexRef.current = (currentIndexRef.current + 1) % alarms.length;
        const currentIndex = currentIndexRef.current;
  
        const currentAlarm = alarms[currentIndex];
        console.log(`🔄 Cycling to alarm ${currentIndex + 1} of ${alarms.length}:`, currentAlarm?.id);
  
        // Set the active marker's position as the new current location
        setCurrentLocation(currentAlarm);
  
        // Update the floating map card text with the corresponding alarm
        setCurrentMarkerIndex(currentIndex);
        setFloatingMapCardText(currentAlarm);
        
        // Extract caller coordinates from EventComments if available
        console.log('Current alarm EventComments:', currentAlarm?.EventComments);
        if (currentAlarm?.EventComments) {
          console.log('Calling extractCallerCoordinates for current alarm');
          extractCallerCoordinates(currentAlarm.EventComments);
        } else {
          console.log('No EventComments found in current alarm');
        }
      } else {
        setFloatingMapCardText(null);
        setCurrentMarkerIndex(0);
        currentIndexRef.current = -1; // Reset index when no alarms
      }
  
      if (alarms.length === 0) {
        // Geocoding is now handled serverside
        setSelectedTab("dashboard");
      }
    }, 12000); // Update every 12 seconds
  
    return () => {
      clearInterval(timer);
    };
  }, [alarms, hasInvalidAddress, agencyLocation]);
  

  useEffect(() => {
    getPosts();
    const timer = setInterval(async () => {
      getPosts();
    }, 60000);

    return () => {
      clearInterval(timer);
    };
  }, [posts]);

  async function getPosts() {
    let currentIndex = -1;
    if (posts.length > 0) {
      currentIndex = (currentIndex + 1) % posts.length;
      setActivePostIndex(currentIndex);

      try {
        const documentSnapshot = await getDoc(
          doc(db, "users", posts[currentIndex].userId)
        );

        if (documentSnapshot.exists() && documentSnapshot.data()?.name) {
          setPostText(
            posts[currentIndex].post + " -- " + documentSnapshot.data().name
          );
        } else {
          setPostText(posts[currentIndex].post);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setPostText(posts[currentIndex].post);
      }
    } else {
      setPostText("");
      setActivePostIndex(-1);
    }
  }

  function checkAlarms() {
    return new Promise((resolve, reject) => {
      var newAlarms = alarms;
      for (let i = 0; i < newAlarms.length; i++) {
        let marker = newAlarms[i];
        let time = marker.TimeStamp;
        let date = time.toDate();
        let now = new Date();

        const timeDifference = Math.abs(date - now);

        // Calculate the number of milliseconds in an hour
        const millisecondsInThreeHours = 3 * 60 * 60 * 1000;
        if (timeDifference > millisecondsInThreeHours) {
          newAlarms.splice(i, 1);
        }
      }
      setAlarms(newAlarms);
      resolve();
    });
  }

  // Memoize containerStyle to prevent unnecessary re-renders
  const containerStyle = useMemo(() => ({
    width: "100vw",
    height: "100vh",
    display: selectedTab === "map" ? "block" : "none",
  }), [selectedTab]);
  
  // Memoize mapOptions to prevent map reloads
  const mapOptions = useMemo(() => ({
    mapTypeId: "hybrid",
    trafficLayer: true,
  }), []);

  // Memoize center to prevent unnecessary map panning
  const mapCenter = useMemo(() => {
    console.log('📍 Map center memoized:', currentLocation);
    return currentLocation;
  }, [currentLocation?.lat, currentLocation?.lng]);

  // Center Mapbox map on current alarm when it changes
  useEffect(() => {
    if (!useGoogleMaps && mapRef.current && alarms.length > 0 && currentMarkerIndex < alarms.length) {
      const currentAlarm = alarms[currentMarkerIndex];
      if (currentAlarm?.lat && currentAlarm?.lng) {
        mapRef.current.flyTo({
          center: [currentAlarm.lng, currentAlarm.lat],
          zoom: 18,
          duration: 1000,
          essential: true
        });
        console.log('🎯 Mapbox centering on alarm:', currentAlarm);
      }
    }
  }, [currentMarkerIndex, alarms, useGoogleMaps]);

  const getMarkerIcon = (callcard) => {
    switch (callcard) {
      case "FIRE":
        return callmarkerfire;
      case "EMS":
        return callmarkerems;
      default:
        return callmarker;
    }
  };

  // ---- Fire hydrants (POI-tile pipeline, mirrors LadderApp) ----
  // Center hydrant loading on the alarm the map is currently showing.
  const hydrantCenter = useMemo(() => {
    const a = alarms[currentMarkerIndex];
    if (a && Number.isFinite(a.lat) && Number.isFinite(a.lng)) return { lat: a.lat, lng: a.lng };
    if (currentLocation && Number.isFinite(currentLocation.lat) && Number.isFinite(currentLocation.lng)) {
      return { lat: currentLocation.lat, lng: currentLocation.lng };
    }
    return null;
  }, [alarms, currentMarkerIndex, currentLocation]);

  // Fetch hydrants for a ~0.1° box around the current alarm (matches LadderApp's
  // HydrantList). Metadata + payload caches make overlapping re-fetches nearly
  // free; errors are swallowed so the map never breaks.
  useEffect(() => {
    if (!agency || !hydrantCenter) return;
    let active = true;
    const HALF = 0.05; // ~0.1° box
    const area = {
      bounds: {
        north: hydrantCenter.lat + HALF,
        south: hydrantCenter.lat - HALF,
        east: hydrantCenter.lng + HALF,
        west: hydrantCenter.lng - HALF,
      },
    };
    (async () => {
      try {
        const { pois } = await loadHydrantsForArea(area, {
          agencies: [agency, ...secondaryAgencies],
        });
        if (active) setHydrants(pois.map((p) => p.poi));
      } catch (err) {
        if (active) console.error("Hydrant load failed", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [agency, secondaryAgencies, hydrantCenter]);

  // Cap markers (each engine creates one DOM/overlay node per marker):
  // nearest-to-center first so the most relevant hydrants always render.
  const MAX_HYDRANT_MARKERS = 150;
  const hydrantMarkers = useMemo(() => {
    if (!hydrantCenter) return [];
    return hydrants
      .filter((h) => Number.isFinite(Number(h.latitude)) && Number.isFinite(Number(h.longitude)))
      .map((h) => ({
        h,
        d: (h.latitude - hydrantCenter.lat) ** 2 + (h.longitude - hydrantCenter.lng) ** 2,
      }))
      .sort((a, b) => a.d - b.d)
      .slice(0, MAX_HYDRANT_MARKERS)
      .map((x) => x.h);
  }, [hydrants, hydrantCenter]);

  // Automatically switch to Google Maps if Mapbox encounters an error
  useEffect(() => {
    if (mapboxError && !useGoogleMaps) {
      console.warn('⚠️ MAPBOX FAILOVER: Switching to Google Maps due to Mapbox error');
      setUseGoogleMaps(true);
    }
  }, [mapboxError, useGoogleMaps]);

  // Only check loading status for Google Maps if we're using it
  if (useGoogleMaps && !isLoaded) {
    return <div>Loading Map...</div>;
  }

  if (useGoogleMaps && loadError) {
    return <div>Error loading maps</div>;
  }

  // Log when component renders
  console.log('🔄 FullScreenMap component render');
  console.log('🗺️ useGoogleMaps state:', useGoogleMaps, 'Type:', typeof useGoogleMaps);
  console.log('🗺️ mapboxError state:', mapboxError);
  console.log('🗺️ Will render:', useGoogleMaps ? 'Google Maps' : 'Mapbox');
  
  return (
    <div style={{ position: "relative" }}>
      {/* Global animations for both map types */}
      <style>
        {`
          @keyframes vehiclePulse {
            0% {
              transform: translate(-50%, -50%) scale(0.5);
              opacity: 0.8;
            }
            70% {
              transform: translate(-50%, -50%) scale(2);
              opacity: 0;
            }
            100% {
              transform: translate(-50%, -50%) scale(0.5);
              opacity: 0;
            }
          }
          @keyframes callerPulse {
            0% {
              transform: translate(-50%, -50%) scale(0.8);
              opacity: 0.9;
            }
            70% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 0.3;
            }
            100% {
              transform: translate(-50%, -50%) scale(0.8);
              opacity: 0.9;
            }
          }
        `}
      </style>
      { !hasInvalidAddress && alarms.length > 0 && currentLocation ? (
        useGoogleMaps ? (
          // GOOGLE MAPS
          console.log('🗺️ GoogleMap component rendering with center:', mapCenter) || <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={20}
          options={mapOptions}
          onLoad={(map) => console.log('🎯 GoogleMap INITIALIZED/LOADED - This means a NEW map API call!')}
          onUnmount={() => console.log('🔴 GoogleMap UNMOUNTED')}
        >
          <TrafficLayer autoRefresh />
          {alarms
            .filter(alarm => {
              const hasValidCoords = !isNaN(alarm.lat) && !isNaN(alarm.lng) && 
                                    alarm.lat !== null && alarm.lng !== null &&
                                    alarm.lat !== undefined && alarm.lng !== undefined;
              if (!hasValidCoords) {
                console.warn('⚠️ Skipping alarm marker with invalid coordinates:', alarm.id, 'lat:', alarm.lat, 'lng:', alarm.lng);
              }
              return hasValidCoords;
            })
            .map((alarm, index) => (
            <Marker
              key={alarm.id}
              position={{ lat: alarm.lat, lng: alarm.lng }}
              title={`Alarm ${index + 1}`}
              icon={{
                url: getMarkerIcon(alarm.callcard),
                scaledSize: new window.google.maps.Size(50, 50),
              }}
            />
          ))}
          {/* Hydrant Markers */}
          {hydrantMarkers.map((h, i) => (
            <Marker
              key={`hyd-${deriveHydrantDocKey(h) || i}`}
              position={{ lat: Number(h.latitude), lng: Number(h.longitude) }}
              title={h.poiTitle || h.HYDRANT_ID || "Hydrant"}
              icon={{
                url: hydrantIconDataUri(),
                scaledSize: new window.google.maps.Size(HYDRANT_ICON_W, HYDRANT_ICON_H),
                anchor: new window.google.maps.Point(HYDRANT_ICON_W / 2, HYDRANT_ICON_H),
              }}
            />
          ))}
          {vehicles
            .filter(vehicle => {
              const hasValidCoords = !isNaN(vehicle.latitude) && !isNaN(vehicle.longitude) && 
                                    vehicle.latitude !== null && vehicle.longitude !== null &&
                                    vehicle.latitude !== undefined && vehicle.longitude !== undefined;
              if (!hasValidCoords) {
                console.warn('⚠️ Skipping vehicle marker with invalid coordinates:', vehicle.responderName, 'lat:', vehicle.latitude, 'lng:', vehicle.longitude);
              }
              return hasValidCoords;
            })
            .map((vehicle) => (
            <OverlayView
              key={vehicle.responderName}
              position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div style={{
                position: 'relative',
                width: '50px',
                height: '50px'
              }} onClick={() => setSelectedVehicle(vehicle)}>
                <div style={{
                  position: 'absolute',
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 0, 0, 0.4)',
                  transform: 'translate(-50%, -50%)',
                  animation: 'vehiclePulse 2s infinite'
                }} />
                <div style={{
                  position: 'absolute',
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#ff0000',
                  border: '2px solid white',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)'
                }} />
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  left: '15%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  fontWeight: 'bold'
                }}>
                  {vehicle.responderName}
                </div>
              </div>
            </OverlayView>
          ))}
          {selectedVehicle && selectedVehicle.type !== "aircraft" && (
            <InfoWindow
              position={{ lat: selectedVehicle.latitude, lng: selectedVehicle.longitude }}
              onCloseClick={() => setSelectedVehicle(null)}
            >
              <div style={{ backgroundColor: '#212c40', color: 'white', padding: '10px', borderRadius: '5px' }}>
                <h4 style={{ margin: 0, color: 'white' }}>{selectedVehicle.responderName}</h4>
                <p style={{ margin: 0 }}><strong>Type:</strong> {selectedVehicle.vehicletype}</p>
                <p style={{ margin: 0 }}><strong>Last Updated:</strong> {new Date(selectedVehicle.timestamp).toLocaleString()}</p>
              </div>
            </InfoWindow>
          )}
          {console.log('About to render caller location:', callerLocation) || (callerLocation && 
           !isNaN(callerLocation.latitude) && !isNaN(callerLocation.longitude) &&
           callerLocation.latitude !== null && callerLocation.longitude !== null &&
           callerLocation.latitude !== undefined && callerLocation.longitude !== undefined) && (
            <OverlayView
              position={{ lat: callerLocation.latitude, lng: callerLocation.longitude }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 255, 0, 0.3)',
                  border: '3px solid #00ff00',
                  transform: 'translate(-50%, -50%)',
                  animation: 'callerPulse 2s infinite'
                }} />
                <div style={{
                  position: 'absolute',
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#00ff00',
                  border: '2px solid white',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)'
                }} />
                <div style={{
                  position: 'absolute',
                  width: '2px',
                  height: '30px',
                  backgroundColor: '#00ff00',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)'
                }} />
                <div style={{
                  position: 'absolute',
                  width: '30px',
                  height: '2px',
                  backgroundColor: '#00ff00',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)'
                }} />
                <div style={{
                  position: 'absolute',
                  top: '35px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  color: '#00ff00',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  fontWeight: 'bold',
                  border: '1px solid #00ff00'
                }}>
                  Caller Location
                </div>
              </div>
            </OverlayView>
          )}
        </GoogleMap>
      ) : (
          // MAPBOX (Default)
          <ReactMapGL
            ref={mapRef}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            initialViewState={{
              longitude: alarms.length > 0 && alarms[currentMarkerIndex]?.lng ? alarms[currentMarkerIndex].lng : currentLocation.lng,
              latitude: alarms.length > 0 && alarms[currentMarkerIndex]?.lat ? alarms[currentMarkerIndex].lat : currentLocation.lat,
              zoom: 18
            }}
            style={{ width: '100vw', height: '100vh', display: selectedTab === "map" ? "block" : "none" }}
            mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
            onLoad={(evt) => {
              console.log('🎯 Mapbox Map INITIALIZED/LOADED');
              // Center on current alarm after map loads
              if (alarms.length > 0 && currentMarkerIndex < alarms.length) {
                const currentAlarm = alarms[currentMarkerIndex];
                if (currentAlarm?.lat && currentAlarm?.lng) {
                  evt.target.flyTo({
                    center: [currentAlarm.lng, currentAlarm.lat],
                    zoom: 18,
                    duration: 1000
                  });
                }
              }
            }}
            onError={(error) => {
              console.error('❌ MAPBOX ERROR:', error);
              console.warn('⚠️ Initiating failover to Google Maps...');
              setMapboxError(true);
            }}
          >
            {/* Alarm Markers */}
            {alarms
              .filter(alarm => {
                const hasValidCoords = !isNaN(alarm.lat) && !isNaN(alarm.lng) && 
                                      alarm.lat !== null && alarm.lng !== null &&
                                      alarm.lat !== undefined && alarm.lng !== undefined;
                if (!hasValidCoords) {
                  console.warn('⚠️ Skipping alarm marker with invalid coordinates:', alarm.id, 'lat:', alarm.lat, 'lng:', alarm.lng);
                }
                return hasValidCoords;
              })
              .map((alarm, index) => (
              <MapboxMarker
                key={alarm.id}
                longitude={alarm.lng}
                latitude={alarm.lat}
                anchor="bottom"
              >
                <img 
                  src={getMarkerIcon(alarm.callcard)} 
                  alt={`Alarm ${index + 1}`}
                  style={{ width: '50px', height: '50px', cursor: 'pointer' }}
                />
              </MapboxMarker>
            ))}

            {/* Hydrant Markers */}
            {hydrantMarkers.map((h, i) => (
              <MapboxMarker
                key={`hyd-${deriveHydrantDocKey(h) || i}`}
                longitude={Number(h.longitude)}
                latitude={Number(h.latitude)}
                anchor="bottom"
              >
                <div
                  title={h.poiTitle || h.HYDRANT_ID || "Hydrant"}
                  style={{
                    width: `${HYDRANT_ICON_W}px`,
                    height: `${HYDRANT_ICON_H}px`,
                    filter: 'drop-shadow(0 1px 1.5px rgba(0,0,0,0.5))',
                    cursor: 'default',
                  }}
                  dangerouslySetInnerHTML={{ __html: hydrantIconSvg() }}
                />
              </MapboxMarker>
            ))}

            {/* Vehicle Markers */}
            {vehicles
              .filter(vehicle => {
                const hasValidCoords = !isNaN(vehicle.latitude) && !isNaN(vehicle.longitude) && 
                                      vehicle.latitude !== null && vehicle.longitude !== null &&
                                      vehicle.latitude !== undefined && vehicle.longitude !== undefined;
                if (!hasValidCoords) {
                  console.warn('⚠️ Skipping vehicle marker with invalid coordinates:', vehicle.responderName, 'lat:', vehicle.latitude, 'lng:', vehicle.longitude);
                }
                return hasValidCoords;
              })
              .map((vehicle) => (
              <MapboxMarker
                key={vehicle.responderName}
                longitude={vehicle.longitude}
                latitude={vehicle.latitude}
                anchor="center"
              >
                <div 
                  style={{
                    position: 'relative',
                    width: '50px',
                    height: '50px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedVehicle(vehicle)}
                >
                  <div style={{
                    position: 'absolute',
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 0, 0, 0.4)',
                    transform: 'translate(-50%, -50%)',
                    animation: 'vehiclePulse 2s infinite'
                  }} />
                  <div style={{
                    position: 'absolute',
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#ff0000',
                    border: '2px solid white',
                    borderRadius: '50%',
                    transform: 'translate(-50%, -50%)',
                    left: '50%',
                    top: '50%'
                  }} />
                  <div style={{
                    position: 'absolute',
                    top: '30px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold'
                  }}>
                    {vehicle.responderName}
                  </div>
                </div>
              </MapboxMarker>
            ))}

            {/* Caller Location Marker */}
            {callerLocation && 
             !isNaN(callerLocation.latitude) && !isNaN(callerLocation.longitude) &&
             callerLocation.latitude !== null && callerLocation.longitude !== null &&
             callerLocation.latitude !== undefined && callerLocation.longitude !== undefined && (
              <MapboxMarker
                longitude={callerLocation.longitude}
                latitude={callerLocation.latitude}
                anchor="center"
              >
                <div style={{ position: 'relative' }}>
                  <div style={{
                    position: 'absolute',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 255, 0, 0.3)',
                    border: '3px solid #00ff00',
                    transform: 'translate(-50%, -50%)',
                    animation: 'callerPulse 2s infinite',
                    left: '50%',
                    top: '50%'
                  }} />
                  <div style={{
                    position: 'absolute',
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#00ff00',
                    border: '2px solid white',
                    borderRadius: '50%',
                    transform: 'translate(-50%, -50%)',
                    left: '50%',
                    top: '50%'
                  }} />
                  <div style={{
                    position: 'absolute',
                    width: '2px',
                    height: '30px',
                    backgroundColor: '#00ff00',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)'
                  }} />
                  <div style={{
                    position: 'absolute',
                    width: '30px',
                    height: '2px',
                    backgroundColor: '#00ff00',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)'
                  }} />
                  <div style={{
                    position: 'absolute',
                    top: '35px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    color: '#00ff00',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold',
                    border: '1px solid #00ff00'
                  }}>
                    Caller Location
                  </div>
                </div>
              </MapboxMarker>
            )}

            {/* Vehicle Info Popup */}
            {selectedVehicle && selectedVehicle.type !== "aircraft" && (
              <MapboxMarker
                longitude={selectedVehicle.longitude}
                latitude={selectedVehicle.latitude}
                anchor="bottom"
                offset={[0, -20]}
              >
                <div style={{ 
                  backgroundColor: '#212c40', 
                  color: 'white', 
                  padding: '10px', 
                  borderRadius: '5px',
                  minWidth: '200px',
                  position: 'relative'
                }}>
                  <button
                    onClick={() => setSelectedVehicle(null)}
                    style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      background: 'transparent',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ×
                  </button>
                  <h4 style={{ margin: '0 0 5px 0', color: 'white' }}>{selectedVehicle.responderName}</h4>
                  <p style={{ margin: '3px 0' }}><strong>Type:</strong> {selectedVehicle.vehicletype}</p>
                  <p style={{ margin: '3px 0' }}><strong>Last Updated:</strong> {new Date(selectedVehicle.timestamp).toLocaleString()}</p>
                </div>
              </MapboxMarker>
            )}
          </ReactMapGL>
        )
      ) : hasInvalidAddress ? (
        // Render the background with agency logo and message
        <div
          style={{
            width: "100vw",
            height: "100vh",
            backgroundColor: "#42474a",
            display: selectedTab === "map" ? "flex" : "none",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            padding: "20px",
          }}
        >
          {agencyLogoUrl ? (
            <>
              <img
                src={agencyLogoUrl}
                alt="Agency Logo"
                style={{
                  maxWidth: "80%",
                  maxHeight: "80%",
                  marginBottom: "20px",
                }}
              />
              <p style={{
                color: "white",
                fontSize: "20px",
                textAlign: "center",
                maxWidth: "80%",
              }}>
                Mapping is not available for this alarm
              </p>
            </>
          ) : (
            <p style={{ color: "white" }}>Loading agency logo...</p>
          )}
        </div>
      ) : (
        <div style={{ 
          width: "100vw", 
          height: "100vh", 
          backgroundColor: "#42474a",
          display: selectedTab === "map" ? "flex" : "none",
          justifyContent: "center",
          alignItems: "center"
        }}>
          <p style={{ color: "white" }}>Loading map...</p>
        </div>
      )}
      <FloatingMapCard
        text={floatingMapCardText}
        callId={floatingMapCardText?.id}
        agency={agency}
        onCallinsChange={setHasCallins}
        isFullscreenMapVisible={selectedTab === "map"}
      />
    </div>
  );
  
  
};

export default FullScreenMap;